// Custom Editor Component
/**
 * CustomEditor Component
 * 
 * A rich-text editor component using CKEditor 5, integrated with Formik for form handling.
 * This component provides a variety of text formatting and media embedding features.
 */
import { useField, useFormikContext } from "formik";
import React from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
    ClassicEditor,
    Autoformat,
    AutoImage,
    AutoLink,
    Autosave,
    Bold,
    Code,
    CodeBlock,
    Emoji,
    Essentials,
    FindAndReplace,
    FontColor,
    FontFamily,
    FontSize,
    GeneralHtmlSupport,
    Heading,
    Highlight,
    HtmlComment,
    HtmlEmbed,
    Image,
    ImageBlock,
    ImageCaption,
    ImageInsert,
    ImageInsertViaUrl,
    ImageResize,
    ImageStyle,
    ImageToolbar,
    ImageUpload,
    PictureEditing,
    Italic,
    Link,
    List,
    ListProperties,
    Mention,
    PageBreak,
    Paragraph,
    PasteFromMarkdownExperimental,
    PasteFromOffice,
    ShowBlocks,
    SpecialCharacters,
    SpecialCharactersArrows,
    SpecialCharactersCurrency,
    SpecialCharactersEssentials,
    SpecialCharactersLatin,
    SpecialCharactersMathematical,
    SpecialCharactersText,
    Strikethrough,
    TextTransformation,
    Underline,
} from "ckeditor5";

import "ckeditor5/ckeditor5.css";
import { useState, useEffect, useRef, useMemo } from "react";
import { toast } from "react-toastify";
import { CmsService } from "@/api/cmsService";

const LICENSE_KEY = "GPL";

// Every format a browser can display (matches the backend's allowed list). CKEditor's own default
// list leaves out SVG, AVIF, ICO and APNG, so those would be refused before reaching the server.
const IMAGE_UPLOAD_TYPES = [
    "jpeg", "png", "apng", "gif", "webp", "avif", "bmp", "x-ms-bmp", "svg+xml", "x-icon", "vnd.microsoft.icon",
];

/**
 * Upload adapter: sends the chosen image to the admin API (stored on S3) and hands the editor the
 * returned URL, so the page content holds a small <img src="https://..."> instead of base64 data.
 */
class CmsImageUploadAdapter {
    constructor(loader) {
        this.loader = loader;
    }

    async upload() {
        const file = await this.loader.file;
        try {
            const url = await CmsService.uploadImage(file);
            if (!url) {
                throw new Error("Upload failed");
            }
            return { default: url };
        } catch (err) {
            const message = err?.response?.data?.message || err?.message || "Image upload failed";
            toast.error(message);
            throw new Error(message);
        }
    }

    abort() {}
}

/** CKEditor plugin that routes image uploads through CmsImageUploadAdapter. */
function CmsImageUploadAdapterPlugin(editor) {
    editor.plugins.get("FileRepository").createUploadAdapter = (loader) => new CmsImageUploadAdapter(loader);
}

/**
 * CustomEditor Component
 * 
 * @param {Object} props Component properties
 * @param {string} props.name The name of the form field
 * @param {string} props.value The initial value of the editor
 * @param {boolean} [props.enableImageUpload=false] Adds the image button (upload from computer / by URL),
 *   captions, alignment and resizing. Off by default so other editors (email/SMS templates) are unchanged.
 * @returns {JSX.Element} The rendered CKEditor component
 */

const CustomEditor = ({ name, value, onChange, enableImageUpload = false }) => {
    const [field, meta] = useField(name);
    const { setFieldValue } = useFormikContext();
    const editorContainerRef = useRef(null);
    const editorRef = useRef(null);
    const [isLayoutReady, setIsLayoutReady] = useState(false);

    useEffect(() => {
        setIsLayoutReady(true);
        return () => setIsLayoutReady(false);
    }, []);
    const { editorConfig } = useMemo(() => {
        if (!isLayoutReady) {
            return {};
        }

        return {
            editorConfig: {
                mediaEmbed: {
                    previewsInData: true
                },
                toolbar: {
                    items: [
                        'heading',
                        '|',
                        'bold',
                        'italic',
                        'underline',
                        'strikethrough',
                        'subscript',
                        'superscript',
                        '|',
                        'link',
                        'blockquote',
                        ...(enableImageUpload ? ['insertImage'] : []),
                        'mediaEmbed',
                        'insertTable',
                        'alignment',
                        '|',
                        'bulletedList',
                        'numberedList',
                        '|',
                        'undo',
                        'redo',
                        '|',
                        'fontColor',
                        'fontBackgroundColor',
                        'fontFamily',
                        'fontSize',
                        '|',
                        'highlight',
                        'horizontalLine',
                        'pageBreak',
                        '|',
                        'indent',
                        'outdent',
                        '|',
                        'findAndReplace',
                        'specialCharacters',
                        'removeFormat',
                        '|'
                    ]
                },
                plugins: [
                    Autoformat,
                    AutoImage,
                    AutoLink,
                    Autosave,
                    Bold,
                    Code,
                    CodeBlock,
                    Emoji,
                    Essentials,
                    FindAndReplace,
                    FontColor,
                    FontFamily,
                    FontSize,
                    GeneralHtmlSupport,
                    Heading,
                    Highlight,
                    HtmlComment,
                    HtmlEmbed,
                    ImageBlock,
                    ImageInsertViaUrl,
                    ImageToolbar,
                    Italic,
                    Link,
                    List,
                    ListProperties,
                    Mention,
                    PageBreak,
                    Paragraph,
                    PasteFromMarkdownExperimental,
                    PasteFromOffice,
                    ShowBlocks,
                    SpecialCharacters,
                    SpecialCharactersArrows,
                    SpecialCharactersCurrency,
                    SpecialCharactersEssentials,
                    SpecialCharactersLatin,
                    SpecialCharactersMathematical,
                    SpecialCharactersText,
                    Strikethrough,
                    TextTransformation,
                    Underline,
                    ...(enableImageUpload
                        ? [Image, ImageCaption, ImageInsert, ImageResize, ImageStyle, ImageUpload, PictureEditing]
                        : []),
                ],
                extraPlugins: enableImageUpload ? [CmsImageUploadAdapterPlugin] : [],
                fontFamily: {
                    supportAllValues: true,
                },
                fontSize: {
                    options: [10, 12, 14, "default", 18, 20, 22],
                    supportAllValues: true,
                },
                heading: {
                    options: [
                        {
                            model: "paragraph",
                            title: "Paragraph",
                            class: "ck-heading_paragraph",
                        },
                        {
                            model: "heading1",
                            view: "h1",
                            title: "Heading 1",
                            class: "ck-heading_heading1",
                        },
                        {
                            model: "heading2",
                            view: "h2",
                            title: "Heading 2",
                            class: "ck-heading_heading2",
                        },
                        {
                            model: "heading3",
                            view: "h3",
                            title: "Heading 3",
                            class: "ck-heading_heading3",
                        },
                        {
                            model: "heading4",
                            view: "h4",
                            title: "Heading 4",
                            class: "ck-heading_heading4",
                        },
                        {
                            model: "heading5",
                            view: "h5",
                            title: "Heading 5",
                            class: "ck-heading_heading5",
                        },
                        {
                            model: "heading6",
                            view: "h6",
                            title: "Heading 6",
                            class: "ck-heading_heading6",
                        },
                    ],
                },
                image: enableImageUpload
                    ? {
                        toolbar: [
                            "toggleImageCaption",
                            "imageTextAlternative",
                            "|",
                            // Block images (own line): left / centre / right all support captions.
                            "imageStyle:alignLeft",
                            "imageStyle:alignCenter",
                            "imageStyle:alignRight",
                            "|",
                            // Inline image (inside a line of text): CKEditor cannot give it a caption,
                            // turning the caption on converts it to a centred block image.
                            "imageStyle:inline",
                            "|",
                            "resizeImage",
                        ],
                        upload: { types: IMAGE_UPLOAD_TYPES },
                        resizeOptions: [
                            { name: "resizeImage:original", value: null, label: "Original" },
                            { name: "resizeImage:50", value: "50", label: "50%" },
                            { name: "resizeImage:75", value: "75", label: "75%" },
                        ],
                    }
                    : {
                        toolbar: ["imageTextAlternative"],
                    },
                initialData: "",
                licenseKey: LICENSE_KEY,
                link: {
                    addTargetToExternalLinks: true,
                    defaultProtocol: "https://",
                    decorators: {
                        toggleDownloadable: {
                            mode: "manual",
                            label: "Downloadable",
                            attributes: {
                                download: "file",
                            },
                        },
                    },
                },
                list: {
                    properties: {
                        styles: true,
                        startIndex: true,
                        reversed: true,
                    },
                },
                placeholder: "Type or paste your content here!",
            },
        };
    }, [isLayoutReady, enableImageUpload]);

    return (
        <div className="ckeditor">
            <div className="main-container">
                <div
                    className="editor-container editor-container_classic-editor"
                    ref={editorContainerRef}
                >
                    <div className="editor-container__editor">
                        <div ref={editorRef}>
                            {editorConfig && (
                                <CKEditor
                                    editor={ClassicEditor}
                                    config={editorConfig}
                                    data={field.value ?? ""}
                                    onChange={(event, editor) => {
                                        const htmlContent = editor.getData();
                                        if (onChange) {
                                          onChange(htmlContent);
                                        }
                                      }}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {meta.touched && meta.error && <div className="text-danger">{meta.error}</div>}
        </div>
    );
};
export default CustomEditor;
